/**
 * OfflineStorage.js
 * Modeled after Stocky's localStorage & IndexedDB offline caching architecture.
 * Safely persists POS catalog, master data snapshots, and local states.
 */

import { isElectronApp, getSyncMode } from "../config/config";

const hasWindow = typeof window !== "undefined";

const getStorage = () => {
  if (!hasWindow) return null;
  try {
    if (window.localStorage) {
      return window.localStorage;
    }
  } catch (e) {
    // Fail gracefully in strict privacy/sandbox modes
  }
  return null;
};

export const readJSON = (key, fallback = null) => {
  const storage = getStorage();
  if (!storage) return fallback;
  try {
    const raw = storage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch (e) {
    return fallback;
  }
};

export const writeJSON = (key, value) => {
  const storage = getStorage();
  if (!storage) return;
  try {
    if (value === undefined || value === null) {
      storage.removeItem(key);
    } else {
      storage.setItem(key, JSON.stringify(value));
    }
  } catch (e) {
    console.warn(`[OfflineStorage] Failed to write key "${key}":`, e);
  }
};

export const POS_BOOTSTRAP_KEY = "restro_pos_bootstrap_v1";
export const POS_OFFLINE_ORDERS_KEY = "restro_offline_orders_v1";
export const POS_LAST_SYNC_KEY = "restro_pos_last_sync_v1";
export const ORDERS_SNAPSHOT_KEY = "restro_orders_snapshot_v1";
export const ORDERS_INIT_KEY = "restro_orders_init_v1";
export const ROOMS_SNAPSHOT_KEY = "restro_rooms_snapshot_v1";
export const ROOMS_TYPES_KEY = "restro_rooms_types_v1";
export const INVOICES_SNAPSHOT_KEY = "restro_invoices_snapshot_v1";
export const INVOICES_INIT_KEY = "restro_invoices_init_v1";
export const DAYBOOK_SNAPSHOT_KEY = "restro_daybook_snapshot_v1";

export const POS_OFFLINE_TOKEN_SEQ_KEY = "restro_offline_token_seq_v1";
export const POS_OFFLINE_INV_SEQ_KEY = "restro_offline_inv_seq_v1";

export const OfflineStorage = {
  /**
   * Calculate Nepali Fiscal Year (e.g. 81/82, 82/83)
   */
  getNepaliFiscalYear() {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth() + 1; // 1-12
    const day = today.getDate();
    const bsYear = year + 57;
    let fyStart;
    if (month < 7 || (month === 7 && day < 16)) {
      fyStart = bsYear - 1;
    } else {
      fyStart = bsYear;
    }
    const startStr = (fyStart % 100).toString().padStart(2, '0');
    const endStr = ((fyStart + 1) % 100).toString().padStart(2, '0');
    return `${startStr}/${endStr}`;
  },

  /**
   * Get next structured offline token number (integer sequence starting from 1, resets daily)
   */
  getNextOfflineTokenNo() {
    const todayStr = new Date().toISOString().slice(0, 10);
    const seqData = readJSON(POS_OFFLINE_TOKEN_SEQ_KEY, { date: todayStr, seq: 0 });
    let nextSeq = 1;
    if (seqData && seqData.date === todayStr && typeof seqData.seq === "number") {
      nextSeq = seqData.seq + 1;
    }
    writeJSON(POS_OFFLINE_TOKEN_SEQ_KEY, { date: todayStr, seq: nextSeq });
    return nextSeq;
  },

  /**
   * Get next structured offline invoice number (e.g. INV-82/83-OFF-001)
   */
  getNextOfflineInvoiceNo() {
    const fy = this.getNepaliFiscalYear();
    const seqData = readJSON(POS_OFFLINE_INV_SEQ_KEY, { fy, seq: 0 });
    let nextSeq = 1;
    if (seqData && seqData.fy === fy && typeof seqData.seq === "number") {
      nextSeq = seqData.seq + 1;
    }
    writeJSON(POS_OFFLINE_INV_SEQ_KEY, { fy, seq: nextSeq });
    const formattedSeq = String(nextSeq).padStart(3, "0");
    return `INV-${fy}-OFF-${formattedSeq}`;
  },

  /**
   * Cache complete POS master catalog
   */
  cacheBootstrap(data, auxiliary = {}) {
    if (!data || typeof data !== "object") return;
    const snapshot = {
      categories: data.categories || [],
      menuItems: data.menuItems || [],
      paymentTypes: data.paymentTypes || [],
      printSettings: data.printSettings || null,
      storeSettings: data.storeSettings || null,
      storeTables: data.storeTables || [],
      serviceCharge: data.serviceCharge || 0,
      loyaltySettings: auxiliary.loyaltySettings || null,
      rooms: auxiliary.rooms || [],
      updatedAt: new Date().toISOString()
    };
    writeJSON(POS_BOOTSTRAP_KEY, snapshot);
    return snapshot;
  },

  /**
   * Get cached master data snapshot
   */
  getCachedBootstrap() {
    return readJSON(POS_BOOTSTRAP_KEY, null);
  },

  /**
   * Cache and retrieve active kitchen orders
   */
  cacheOrders(orders) {
    if (Array.isArray(orders)) {
      writeJSON(ORDERS_SNAPSHOT_KEY, orders);
    }
  },
  getCachedOrders() {
    return readJSON(ORDERS_SNAPSHOT_KEY, []);
  },
  addLocalOrder(tableGroup) {
    if (!tableGroup) return;
    const current = this.getCachedOrders();
    const list = Array.isArray(current) ? [...current] : [];
    // If matching table_id or room or same group exists, merge into it
    const existingIdx = list.findIndex(tg => 
      (tableGroup.table_id && tg.table_id === tableGroup.table_id) ||
      (tableGroup.room_name && tg.room_name === tableGroup.room_name) ||
      (tableGroup.table_title && tg.table_title === tableGroup.table_title)
    );
    if (existingIdx >= 0) {
      const existing = list[existingIdx];
      const mergedOrders = [...(existing.orders || []), ...(tableGroup.orders || [])];
      list[existingIdx] = { ...existing, orders: mergedOrders };
    } else {
      list.unshift(tableGroup);
    }
    this.cacheOrders(list);
  },
  removeLocalOrder(orderIds) {
    if (!Array.isArray(orderIds) || orderIds.length === 0) return;
    const current = this.getCachedOrders();
    const stringIds = orderIds.map(String);
    const updated = (Array.isArray(current) ? current : []).map(tg => {
      const remainingOrders = (tg.orders || []).filter(o => !stringIds.includes(String(o.id)));
      if (remainingOrders.length === 0) return null;
      return { 
        ...tg, 
        orders: remainingOrders,
        order_ids: (tg.order_ids || []).filter(id => !stringIds.includes(String(id)))
      };
    }).filter(Boolean);
    this.cacheOrders(updated);
  },

  updateLocalOrderId(oldId, newId, tokenNo = null) {
    if (!oldId || !newId) return;
    const current = this.getCachedOrders();
    const oldIdStr = String(oldId);
    const updated = (Array.isArray(current) ? current : []).map(tg => {
      let matched = false;
      const newOrders = (tg.orders || []).map(o => {
        if (String(o.id) === oldIdStr) {
          matched = true;
          return {
            ...o,
            id: newId,
            token_no: tokenNo || o.token_no,
            _synced: true
          };
        }
        return o;
      });
      if (matched) {
        const newOrderIds = (tg.order_ids || []).map(id => String(id) === oldIdStr ? newId : id);
        return { ...tg, orders: newOrders, order_ids: newOrderIds };
      }
      return tg;
    });
    this.cacheOrders(updated);
  },

  /**
   * Cache and retrieve orders init data
   */
  cacheOrdersInit(data) {
    if (data && typeof data === 'object') {
      writeJSON(ORDERS_INIT_KEY, data);
    }
  },
  getCachedOrdersInit() {
    return readJSON(ORDERS_INIT_KEY, null);
  },

  /**
   * Cache and retrieve PMS rooms & types
   */
  cacheRooms(rooms, types = []) {
    if (Array.isArray(rooms)) {
      const sanitized = rooms.filter(r => r && r.name && String(r.name).trim() !== '' && String(r.name).trim() !== 'null' && !String(r.name).includes('16000001'));
      writeJSON(ROOMS_SNAPSHOT_KEY, sanitized);
    }
    if (Array.isArray(types) && types.length > 0) writeJSON(ROOMS_TYPES_KEY, types);
  },
  getCachedRooms() {
    const list = readJSON(ROOMS_SNAPSHOT_KEY, []);
    return (Array.isArray(list) ? list : []).filter(r => r && r.name && String(r.name).trim() !== '' && String(r.name).trim() !== 'null' && !String(r.name).includes('16000001'));
  },
  getCachedRoomTypes() {
    return readJSON(ROOMS_TYPES_KEY, []);
  },

  /**
   * Cache and retrieve invoices
   */
  cacheInvoices(invoices) {
    if (Array.isArray(invoices)) writeJSON(INVOICES_SNAPSHOT_KEY, invoices);
  },
  getCachedInvoices() {
    return readJSON(INVOICES_SNAPSHOT_KEY, []);
  },
  addLocalInvoice(invoice) {
    if (!invoice) return;
    const current = this.getCachedInvoices();
    const list = Array.isArray(current) ? [...current] : [];
    // Prevent duplicate
    const exists = list.some(inv => String(inv.id) === String(invoice.id) || String(inv.invoice_id) === String(invoice.invoice_id));
    if (!exists) {
      list.unshift(invoice);
      this.cacheInvoices(list);
    }
  },
  cacheInvoicesInit(data) {
    if (data && typeof data === 'object') writeJSON(INVOICES_INIT_KEY, data);
  },
  getCachedInvoicesInit() {
    return readJSON(INVOICES_INIT_KEY, null);
  },

  /**
   * Cache and retrieve financial daybook
   */
  cacheDayBook(daybookData) {
    if (daybookData && typeof daybookData === 'object') {
      writeJSON(DAYBOOK_SNAPSHOT_KEY, daybookData);
    }
  },
  getCachedDayBook() {
    return readJSON(DAYBOOK_SNAPSHOT_KEY, null);
  },

  /**
   * Check if client browser is currently reporting online
   */
  isClientOnline() {
    if (!hasWindow) return true;
    try {
      // Electron desktop app always has its bundled local SQLite backend (localhost:5001) reachable even without internet.
      if (isElectronApp()) return true;
    } catch (e) {}
    try {
      return window.navigator ? window.navigator.onLine !== false : true;
    } catch (e) {
      return true;
    }
  },

  /**
   * Clear master caches (retaining queued unsynced offline sales!)
   */
  clearCatalogCache() {
    try {
      const storage = getStorage();
      if (storage) {
        storage.removeItem(POS_BOOTSTRAP_KEY);
      }
    } catch (e) {}
  }
};

export default OfflineStorage;
